import React, { useState, useEffect, useCallback, useRef } from "react";
import BasicRouter from "./BasicRouter";
import {
  applyStationId,
  loginPost,
  loginToken,
  persistRequestedStationIdFromUrl,
  setUserEntry,
  hydrateBranchFromProfile,
} from "./server/Customer";
import { getCart } from "./server/CollectSite";
import { setToggleCart } from "./server/CartsOpen";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./components/header/Header";
import Footer from "./components/footer/Footer";
import Preload from "./components/Preload";
import OpenText from "./components/OpenText";
import { customerList, customerListClear } from "./server/ShoppingList";
import { SystemMessagePopUp } from "./components/containers/PopUp";
import UserResetEmail from "./components/header/UserEmailReset";
import PopAddress from "./components/routs/address/PopAddress";
import ProductPageWithImg from "./components/routs/productPage/ProductPageWithImg";
import HeaderConnection from "./components/header/HeaderConnection";
import AdminDashboard from "./components/routs/adminDashboard/AdminDashboard";
import StatusBar from "./components/StatusBar";
import CartDrawer from "./components/cartDrawer/CartDrawer";
import MobileNavDrawer from "./components/header/MobileNavDrawer";
import { OpenTextGet } from "./server/OpenText";
import { updateMetaTags } from "./utils/metaTags";
import AuthDialog from "./components/header/AuthDialog";
import useAuthTabSync from "./hooks/useAuthTabSync";
import useTokenExpiry from "./hooks/useTokenExpiry";
import {
  handleExpiredSession,
  shouldExpireTokenNow,
} from "./auth/sessionExpiry";
import {
  hasPendingEmailVerification,
  isEmailVerificationPopupOpen,
  restorePendingEmailVerification,
} from "./utils/emailVerification";
import { canUseCustomerFeatures } from "./utils/userMode";

const App = () => {
  useAuthTabSync();
  useTokenExpiry();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user);
  const preload = useSelector((state) => state.openCart.preload);
  const isClientView = useSelector((state) => state.category.isClientView);
  const { user, role, userHaveReset, stationId, emailVerified } = userState;
  const customerFeatures = canUseCustomerFeatures(role, isClientView, user);
  const emailVerificationPopupOpen = isEmailVerificationPopupOpen({
    user: userState,
  });

  const [canPay, setCanPay] = useState(true);
  const [startPopUp, setStartPopUp] = useState(true);
  const [currentSubCategory, setCurrentSubCategory] = useState(null);
  const [showStationPopUp, setShowStationPopUp] = useState(true);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [allProduct, setAllProduct] = useState(true);

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const haveGetToken = useCallback(() => {
    if (hasPendingEmailVerification()) {
      return;
    }

    dispatch(setUserEntry([true, "login"]));

    const val = {
      userName: "guest",
      password: "a1234567",
      grant_type: "password",
    };
    dispatch(loginToken(val, false));
  }, [dispatch]);

  const getToken = useCallback(() => {
    if (hasPendingEmailVerification()) {
      return;
    }

    const jwtToken = localStorage.getItem("jwtToken");
    if (!jwtToken) {
      haveGetToken();
    } else if (shouldExpireTokenNow(jwtToken)) {
      handleExpiredSession({ dispatch, navigate: navigateRef.current });
    } else {
      const isGuest = localStorage.getItem("userRole")?.includes("guest");
      if (isGuest) {
        dispatch(setUserEntry([true, "login"]));
      }
      dispatch(loginPost(false));
    }
  }, [dispatch, haveGetToken]);

  useEffect(() => {
    const fromUrl = persistRequestedStationIdFromUrl(search);
    if (fromUrl) applyStationId(dispatch, fromUrl);
  }, [dispatch, search, role]);

  useEffect(() => {
    restorePendingEmailVerification(dispatch, userHaveReset);
    if (hasPendingEmailVerification()) {
      dispatch(hydrateBranchFromProfile());
    }
    // Restore pending verification once on mount (e.g. after refresh).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const getProducts = useCallback(() => {
    if (customerFeatures) {
      dispatch(getCart(stationId));
    }
  }, [dispatch, customerFeatures, stationId]);

  useEffect(() => {
    getToken();
  }, [getToken]);

  // Fetch login message and update meta tags after token is available
  useEffect(() => {
    const jwtToken = localStorage.getItem("jwtToken");
    if (!jwtToken || shouldExpireTokenNow(jwtToken)) {
      return;
    }

    OpenTextGet("loginMessage")
      .then((response) => {
        if (response.data && response.data.length > 0) {
          const message = response.data[0].Text;
          if (message) {
            // Strip HTML tags and trim to reasonable length for meta description
            const plainText = message
              .replace(/<[^>]*>/g, "") // Remove HTML tags
              .replace(/\s+/g, " ") // Collapse whitespace
              .trim();
            const metaDescription = plainText.substring(0, 160); // Limit to 160 chars for SEO
            updateMetaTags(metaDescription);
          }
        }
      })
      .catch(() => {
        // Silent fail - keep default meta tags
      });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const isGuest = localStorage.getItem("userRole")?.includes("guest");
    if (!user || isGuest || emailVerified === false) {
      dispatch(setToggleCart(false));
      dispatch(customerListClear());
    } else if (customerFeatures) {
      getProducts();
      dispatch(customerList());
      if (user?.id) {
        setShippingAddress(user);
      }
    } else {
      dispatch(setToggleCart(false));
      dispatch(customerListClear());
      if (user?.id) {
        setShippingAddress(user);
      }
    }
  }, [user, dispatch, customerFeatures, getProducts, emailVerified]);

  const [productPopUp, setProductPopUp] = useState(false);
  const [selectedProd, setSelectedProd] = useState(null);
  const [pendingScrollSlug, setPendingScrollSlug] = useState(null);

  if (pathname.startsWith("/admin") && role) {
    return (
      <div className="admin-mode">
        <div className="header-wrapper">
          <HeaderConnection
            setAllProduct={setAllProduct}
            user={user}
            role={role}
            stationId={stationId}
            basicHeader={true}
            haveGetToken={haveGetToken}
          />
        </div>
        <AdminDashboard />
        {preload ? <Preload /> : null}
        <BasicRouter
          allProduct={allProduct}
          canPay={canPay}
          closePayPopup={() => setCanPay(false)}
          setCurrentSubCategory={setCurrentSubCategory}
          currentSubCategory={currentSubCategory}
          setProductPopUp={setProductPopUp}
          setSelectedProd={setSelectedProd}
        />
        <StatusBar />
      </div>
    );
  }

  const isGuest = localStorage.getItem("userRole")?.includes("guest");
  const showAddressPopUp =
    showStationPopUp && user && !role && !isGuest && emailVerified !== false;

  return (
    <div>
      {showAddressPopUp ? (
        <PopAddress
          setShowStationPopUp={setShowStationPopUp}
          shippingAddress={shippingAddress}
        ></PopAddress>
      ) : null}
      {startPopUp && user && !role && !showAddressPopUp ? (
        <SystemMessagePopUp width="500px" close={() => setStartPopUp(false)}>
          <OpenText ShowHome={false} />
        </SystemMessagePopUp>
      ) : null}
      <Header
        setAllProduct={setAllProduct}
        currentSubCategory={currentSubCategory}
        haveGetToken={haveGetToken}
        setProductPopUp={setProductPopUp}
        setSelectedProd={setSelectedProd}
        setPendingScrollSlug={setPendingScrollSlug}
      />
      {preload ? <Preload /> : null}
      <main>
        <BasicRouter
          allProduct={allProduct}
          canPay={canPay}
          closePayPopup={() => setCanPay(false)}
          setCurrentSubCategory={setCurrentSubCategory}
          currentSubCategory={currentSubCategory}
          setProductPopUp={setProductPopUp}
          setSelectedProd={setSelectedProd}
          pendingScrollSlug={pendingScrollSlug}
          setPendingScrollSlug={setPendingScrollSlug}
        />
      </main>
      {productPopUp && (
        <ProductPageWithImg
          selectedProd={selectedProd}
          close={() => setProductPopUp(false)}
        />
      )}
      <CartDrawer
        setProductPopUp={setProductPopUp}
        setSelectedProd={setSelectedProd}
      />
      <MobileNavDrawer />
      <StatusBar />
      <Footer />
      {emailVerificationPopupOpen ? (
        <UserResetEmail
          Email={userHaveReset.email}
          Password={userHaveReset.password}
          customerId={userHaveReset.customerId}
          emailPassword={userHaveReset.passwordMail}
        />
      ) : null}
      <AuthDialog />
    </div>
  );
};

export default App;
